import {useCallback, useEffect, useRef, useState} from 'react';
import i18next from 'i18next';
import type {PendingChatAttachment} from '../../aiMessageGenerator/utils/types';
import {isImageFile} from '../../aiMessageGenerator/utils/attachmentHelpers';
import {bounceMessage} from '../../../layout/userMessages/UserMessage';
import {isAiAgentVisionImage} from '../utils/fileToDataUrl';

const ACCEPTED_FILE_TYPES = 'image/jpeg,image/jpg,image/png,image/webp';

const warnUnsupportedFile = () =>
    bounceMessage(i18next.t('imageUnsupported', {ns: 'aiAgent'}), 'warning');

/**
 * `rejected` separates "a file was offered and we cannot read it" from "no file here at
 * all" — pasting plain text must stay silent, pasting a PDF must not.
 */
type ClipboardImageResult = {file: File | null; rejected: boolean};

function getClipboardImageFile(clipboardData: DataTransfer | null): ClipboardImageResult {
    if(!clipboardData) {
        return {file: null, rejected: false};
    }

    const files = Array.from(clipboardData.files ?? []);
    const itemFiles = Array.from(clipboardData.items ?? [])
        .filter(item => item.kind === 'file')
        .map(item => item.getAsFile())
        .filter((file): file is File => !!file);

    const supported = [...files, ...itemFiles].find(isAiAgentVisionImage);
    if(supported) {
        return {file: supported, rejected: false};
    }

    return {file: null, rejected: !!files.length || !!itemFiles.length};
}

export function useAiAgentAttachment(onHasAttachmentChange?: (hasAttachment: boolean) => void) {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const pendingAttachmentRef = useRef<PendingChatAttachment | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [pendingFileName, setPendingFileName] = useState<string | null>(null);

    const forgetPendingAttachment = useCallback(() => {
        pendingAttachmentRef.current = null;
        setPreviewUrl(null);
        setPendingFileName(null);
        onHasAttachmentChange?.(false);
    }, [onHasAttachmentChange]);

    const clearPendingAttachment = useCallback(() => {
        if(pendingAttachmentRef.current?.previewUrl) {
            URL.revokeObjectURL(pendingAttachmentRef.current.previewUrl);
        }
        forgetPendingAttachment();
    }, [forgetPendingAttachment]);

    const onHasAttachmentChangeRef = useRef(onHasAttachmentChange);
    onHasAttachmentChangeRef.current = onHasAttachmentChange;

    // The blob URL outlives this component unless it is released on unmount, and the
    // parent still thinks an attachment is pending unless it is told otherwise.
    useEffect(() => () => {
        if(pendingAttachmentRef.current?.previewUrl) {
            URL.revokeObjectURL(pendingAttachmentRef.current.previewUrl);
        }
        if(pendingAttachmentRef.current) {
            pendingAttachmentRef.current = null;
            onHasAttachmentChangeRef.current?.(false);
        }
    }, []);

    const setPendingFile = useCallback((file: File) => {
        clearPendingAttachment();
        const isImage = isImageFile(file);
        const nextPreviewUrl = isImage ? URL.createObjectURL(file) : '';
        pendingAttachmentRef.current = {file, previewUrl: nextPreviewUrl};
        setPreviewUrl(nextPreviewUrl || null);
        setPendingFileName(isImage ? null : file.name);
        onHasAttachmentChange?.(true);
    }, [clearPendingAttachment, onHasAttachmentChange]);

    const handleAttachClick = useCallback(() => {
        fileInputRef.current?.click();
    }, []);

    const handleFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if(!file) {
            return;
        }
        // The picker's `accept` is only a hint — a user can still choose a PDF or a Word
        // file, and dropping it silently looks like the attach button is broken.
        if(!isAiAgentVisionImage(file)) {
            warnUnsupportedFile();
            return;
        }
        setPendingFile(file);
    }, [setPendingFile]);

    const handlePaste = useCallback((event: React.ClipboardEvent) => {
        const {file, rejected} = getClipboardImageFile(event.clipboardData);
        if(rejected) {
            event.preventDefault();
            warnUnsupportedFile();
            return;
        }
        if(!file) {
            return;
        }
        event.preventDefault();
        setPendingFile(file);
    }, [setPendingFile]);

    // The caller takes ownership of the blob URL, so it must not be revoked here.
    const takePendingAttachment = useCallback(() => {
        const pendingAttachment = pendingAttachmentRef.current;
        forgetPendingAttachment();
        return pendingAttachment;
    }, [forgetPendingAttachment]);

    return {
        fileInputRef,
        previewUrl,
        pendingFileName,
        acceptedFileTypes: ACCEPTED_FILE_TYPES,
        handleAttachClick,
        handleFileChange,
        handlePaste,
        handleRemoveAttachment: clearPendingAttachment,
        takePendingAttachment,
    };
}
